import { css, useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import { AddIcon, BankAccountIcon, CurrencyUsdIcon } from '@arcade/web/icons/react'
import { InstrumentType } from '@cash-web/protos/squareup/common/instrument.pb'
import { CashInstrumentType } from '@cash-web/protos/squareup/franklin/cash_instrument_type.pb'
import { replaceWithCDNImageUrl } from '@cash-web/shared-util-url'

import { CardImage } from '../CardImage/CardImage'

const TEN_PERCENT_OPACITY = '1A'

export type Size = 'small' | 'medium'

export const logoSizes: Record<Size, { rem: string; width: number; borderRadius: string; iconSize: 16 | 24 }> = {
  small: { rem: '2.5rem', width: 40, borderRadius: '0.75rem', iconSize: 16 },
  medium: { rem: '3rem', width: 48, borderRadius: '0.75rem', iconSize: 24 },
}

const cardBrandColors = {
  [InstrumentType.VISA]: { background: '#1A1F71' },
  [InstrumentType.MASTER_CARD]: { background: '#000000' },
  [InstrumentType.AMERICAN_EXPRESS]: { background: '#016FD0' },
}

export const Logo = styled.div<{ background?: string; size: Size }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.background ?? props.theme.colors.formFieldBackground};
  overflow: hidden;

  ${props => {
    return css`
      width: ${logoSizes[props.size].rem};
      min-width: ${logoSizes[props.size].rem};
      height: ${logoSizes[props.size].rem};
      border-radius: ${logoSizes[props.size].borderRadius};
    `
  }}
`

const LogoImage = styled.img`
  width: 100%;
  flex: 0 0 auto;
`

type DebitCardAndBankProps = {
  type: CashInstrumentType.DEBIT_CARD | CashInstrumentType.BANK_ACCOUNT
  iconUrl?: string
  cardBrand?: InstrumentType
  bankName?: string
  skeleton?: boolean
  size: Size
}

type CashBalanceProps = {
  type: CashInstrumentType.CASH_BALANCE
  iconUrl?: undefined
  cardBrand?: undefined
  bankName?: undefined
  size: Size
}

type AddInstrumentProps = {
  type: 'ADD_INSTRUMENT'
  iconUrl?: undefined
  cardBrand?: undefined
  bankName?: undefined
  size: Size
}

type InstrumentLogoProps = DebitCardAndBankProps | CashBalanceProps | AddInstrumentProps

type InstrumentTypesWithBrandColors = InstrumentType.VISA | InstrumentType.MASTER_CARD | InstrumentType.AMERICAN_EXPRESS

export const InstrumentLogo = ({ type, iconUrl, cardBrand, bankName, size, ...rest }: InstrumentLogoProps) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const fallbackLogo: { [key in InstrumentLogoProps['type']]: { icon: React.ReactNode; backgroundColor?: string } } = {
    [CashInstrumentType.DEBIT_CARD]: {
      icon:
        'skeleton' in rest && rest.skeleton ? null : (
          <CardImage
            brand={cardBrand ?? InstrumentType.UNKNOWN}
            aria-label={t('Debit card', { context: 'Alt text for debit card image.' })}
          />
        ),
      backgroundColor:
        'skeleton' in rest && rest.skeleton
          ? theme.colors.placeholderIcon
          : cardBrandColors[cardBrand as InstrumentTypesWithBrandColors]
          ? cardBrandColors[cardBrand as InstrumentTypesWithBrandColors].background
          : theme.colors.formFieldBackground,
    },
    [CashInstrumentType.BANK_ACCOUNT]: {
      icon: <BankAccountIcon size={logoSizes[size].iconSize} data-testid="bank-account-icon" />,
    },
    [CashInstrumentType.CASH_BALANCE]: {
      icon: (
        <CurrencyUsdIcon
          size={logoSizes[size].iconSize}
          aria-hidden="true"
          data-testid="cash-balance-logo"
          variant="inverse"
        />
      ),
      backgroundColor: theme.colors.primary,
    },
    ADD_INSTRUMENT: {
      icon: <AddIcon size={logoSizes[size].iconSize} data-testid="add-instrument-plus" />,
      backgroundColor: `${theme.colors.primary}${TEN_PERCENT_OPACITY}`,
    },
  }

  return iconUrl ? (
    <Logo size={size}>
      <LogoImage
        alt={t('{bankName} logo', {
          context: 'Alt text for bank logo image.',
          bankName,
        })}
        src={replaceWithCDNImageUrl({ url: iconUrl, width: logoSizes[size].width })}
      />
    </Logo>
  ) : (
    <Logo background={fallbackLogo[type].backgroundColor} size={size} data-testid="logo-container">
      {fallbackLogo[type].icon}
    </Logo>
  )
}
